import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';
import { IAuthStrategy } from '../interfaces/auth-strategy.interface';
import { API_CLIENT_CONFIG } from '../tokens/api-config.token';

/**
 * 社外アクセス：トークン取得APIのレスポンスインターフェース
 */
interface TokenAcquireResponse {
  message: {
    id: string; // 正常終了時は "EMS00001"
    msg: string;
  };
  /** 正常終了時はアクセストークン文字列が格納される */
  data: string;
}

/**
 * 社外（外網）環境専用の認証サービス
 * @description WING基盤およびWISE連携の仕様に基づき、トークン取得とセッション切れ時の再ログイン誘導を制御します。
 */
@Injectable()
export class ExternalAuthService implements IAuthStrategy {
  private readonly http = inject(HttpClient);
  private readonly config = inject(API_CLIENT_CONFIG);
  private readonly router = inject(Router);
  /**
   * APIのエントリーポイント: /get_token (GETメソッド)
   */
  async acquireToken(): Promise<void> {
    if (!this.config.authApiEnabled) {
      console.log('社外認証APIは無効化されています。ダミートークンを使用します。');
      sessionStorage.setItem('access_token', this.config.dummyToken || '');
      return;
    }

    const targetAuthUrl = (this.config as any).externalAuthUrl || this.config.authUrl;

    // await this.handleTokenRefreshOrRedirect();
    try {
      const timeoutMs = this.config.apiRequestUrlTimeout ?? 120000;

      // HTTPヘッダ項目: Cookie（access_token=<access_token>）をサーバーに自動送信するため
      // 必ず { withCredentials: true } を付与して GET リクエストを実行します。
      const response = await firstValueFrom(this.http.get<TokenAcquireResponse>(targetAuthUrl, { withCredentials: true }).pipe(timeout(timeoutMs)));

      // 「EMS00001」正常終了をオプショナルチェーンで安全に判定
      if (response?.message?.id === 'EMS00001' && response?.data) {
        // レンスポンスボディ: data（アクセストークン文字列）をストレージに保存
        console.log('🔑 社外トークン取得成功: ', response.data);
        sessionStorage.setItem('access_token', response.data);
      } else {
        // 返却パラメータ（異常系：EMS00002等の場合）
        const errorId = response?.message?.id || 'UNKNOWN_ERR';
        const errorMsg = response?.message?.msg || 'access_tokenを取得できない';
        throw new Error(`[${errorId}] トークン取得失敗: ${errorMsg}`);
      }
    } catch (error: any) {
      if (error instanceof HttpErrorResponse) {
        console.error(`❌ トークン取得HTTPエラー [Status: ${error.status}]`, error);

        if (error.status === 401) {
          // アクセス拒否 (401)
          console.warn('🔑 401 アクセス拒否: 認証情報を確認できません。');
        } else if (error.status === 500) {
          // システムエラー (500)
          console.error('💥 500 システムエラー: 認証サーバー側で致命的な障害が発生しています。');
        }

        // サーバから戻されたエラーJSONの解析を試みる
        const serverErrorResponseBody = error.error as TokenAcquireResponse;
        const serverErrorMsgBlock = serverErrorResponseBody?.message;

        if (serverErrorMsgBlock?.id) {
          console.error(`[サーバー返却エラー] コード: ${serverErrorMsgBlock.id}, 内容: ${serverErrorMsgBlock.msg}`);
        }
      } else {
        // HTTP通信以外のエラー（タイムアウト、JSDoc例外など）
        console.error('⚠️ 予期しない認証処理エラー（ネットワーク切断、またはタイムアウト等）', error);
      }

      // トークン取得失敗時は、セッションをクリアしてWISE等のログイン画面へ誘導
      await this.handleTokenRefreshOrRedirect();
      throw error;
    }
  }

  /**
   * 社外用の有効期限切れフロー（エラー画面への強制リダイレクト）
   */
  async handleTokenRefreshOrRedirect(): Promise<void> {
    sessionStorage.clear();
    this.router.navigate(['/mp-app/error'], {
      state: {
        screenTitle: '変額保険ポータル',
        wizardDisplayType: '0',
        wizardLevel: '0',
        errorCode: null,
        errorMessage: null,
      },
    });
    throw new Error('セッション期限切れのため、エラー画面へ遷移します。');
  }
}
